import {
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Component, QueryList } from '@angular/core';
import { Observable, Subscription, of, interval } from 'rxjs';
import { map, mergeMap, switchMap, filter } from "rxjs/operators";
import { AngularFirestore } from "@angular/fire/firestore";
import { ActivatedRoute } from "@angular/router";
import { MagliveApiClient } from "../../../lib/api-client/maglive-api-client";
import { MagticketApiClient } from "../../../lib/api-client/magticket-api-client";
import { Magazine } from "../../../lib/entity/magazine";
import { webSocket, WebSocketSubject } from "rxjs/webSocket";
// noinspection ES6UnusedImports
import {
  Live,
  LiveCommentCount,
  LiveOptionsViewerSegment,
  LiveReactionCount,
  LiveViewerCount,
  LiveViewerCount2,
  LiveTicketOrder
} from "../../../lib/entity/live";
import { environment } from "../../../env/environment";
import { PlayerComponent } from "../../parts/player/player.component";
import { LiveUserAuthority } from "../../../lib/entity/live-user-authority";
import { Mag2LocalStorage } from "../../../lib/mag2-local-storage";
import { FirestoreAccess } from 'src/lib/firestore-access';
import { CookieService } from "ngx-cookie-service";
import { Config } from "../../../lib/entity/config"
import { GiftAnimationReq } from 'src/lib/entity/gift-point';
import { ConsoleLogger } from 'src/lib/console-logger';

type LiveViewResult = Live | "NOT_FOUND" | "NOT_LOGIN" | "NOT_SUBSCRIBER" | "NOT_TICKET" | "NOT_LOGIN_SNEAK_PEEK" | "NOT_SUBSCRIBER_SNEAK_PEEK";

@Component({
  templateUrl: './live-page.component.html',
  styleUrls: ['./live-page.component.scss']
})
export class LivePageComponent implements OnInit, OnDestroy, AfterViewInit {

  baseURL = environment.baseURL;
  loginCallbackURL = environment.baseURL.mag2mypage + 'Welcome.do?ref=' + window.location.href;

  /* Subscription */
  private routeParamsSub: Subscription | undefined;
  private websocketSubject: WebSocketSubject<any> | undefined;
  private countPollingTimer: Subscription;
  private authorityPollingTimer: Subscription;
  private sneakPeekTimer: Subscription;
  private getWatchDataSub: Subscription | undefined;
  private getConfigSub: Subscription | undefined;
  private timerForDelay: Subscription | undefined = undefined;
  private getTicketsSub: Subscription | undefined;

  /**
   * サーバサイドから取得するカウント類 (視聴者数) のリフレッシュ間隔.
   */
  private countPollingMillis: number = 1000 * 10; // 10秒

  /**
   * まぐまぐアカウントのログインセッションが30分で切れてしまうため、視聴画面から定期ポーリングする対応.
   */
  private authorityPollingMillis: number = 1000 * 60 * 10; // 10分

  private checkUserAgentTimer;

  magazine$: Observable<Magazine | string> | 'NOT_INITIALIZED' = "NOT_INITIALIZED";
  live$: Observable<Live | string> | 'NOT_INITIALIZED' = "NOT_INITIALIZED";
  authority: LiveUserAuthority | undefined;

  private previousLive: Live;
  private previousLiveViewResult: LiveViewResult;

  viewerCount$: Observable<LiveViewerCount | string>;

  commentCount$: Observable<LiveCommentCount>;
  reactionGoodCount$: Observable<LiveReactionCount>;

  sneakPeekFinishedReason: "NOT_LOGIN_SNEAK_PEEK" | "NOT_SUBSCRIBER_SNEAK_PEEK";

  following: boolean;
  followModalOpened = false;
  private token: string | undefined;

  ticketEventId: number | undefined;
  tickets: LiveTicketOrder[] | undefined;

  isShowGiftComponent = false;
  isShowGiftConfirmComponent = false;
  isShowLoginAlert = false
  actionText: string
  isShowVariantModal = false
  selectedVariant = 'auto'
  isShowShareModal = false
  isShowDialog = true
  isScreenRotated = false

  /**
   * For Aws mode
   */
  // 子componentで読み込むためにpublicへ変更（継承しているわけではない）
  public isAws: boolean;
  public config: Config;
  // 総視聴者数
  public viewerCount2$: Observable<LiveViewerCount2 | object>;
  private wsReqMessage: Object = { "action": "get_count" };
  private countPollingMillisWs: number = 1000 * 10; // 10秒
  // 初回視聴開始が成功したかどうか
  public isSuccessFirstView: boolean = false;
  // 視聴が購読者限定の時に使用。視聴者が購読者かどうか
  public isSubscriber: boolean = true;
  public canViewLive: boolean = false;
  // 遅延処理用 (読者限定切り替えと終了時に使用)
  private endBroadcastDelayTimeSec: number | undefined = undefined;
  // cookie処理用
  private cookieKey: string;
  // メンテナンスページ用
  isMaintenance: Boolean = false;

  @ViewChildren(PlayerComponent) playerComponent: QueryList<PlayerComponent>;
  @ViewChild(PlayerComponent) playerChildComponent: PlayerComponent;

  constructor(
    private afs: AngularFirestore,
    private route: ActivatedRoute,
    private cookieService: CookieService,
    private firestoreAccess: FirestoreAccess,
    private logger: ConsoleLogger,
  ) {
  }

  static disableTouchMove = (e: TouchEvent) => {
    try {
      e.preventDefault()
    } catch (e) {
    }
  }

  ngOnInit() {
    // Configデータの取得
    this.getConfigSub = this.firestoreAccess.getConfig().subscribe(x => {
      this.config = x.shift();
      // 遅延秒数
      this.endBroadcastDelayTimeSec = Number(this.config.changeBroadcastTargetDelayTimeSec)
      // 基盤切り替え用
      this.isAws = Boolean(this.config.isAwsMode);
      if (environment.name === 'local' || environment.name === 'stage') {
        this.logger.info(this.constructor.name, 'ngOnInit', 'isAws', this.isAws);
        this.logger.info(this.constructor.name, 'ngOnInit', 'delayTimer', this.endBroadcastDelayTimeSec);
      }
      this.ngInitProcess()
    })
  }

  ngAfterViewInit() {
    this.checkUserAgent();
  }

  ngInitProcess() {
    // コメント欄のスクロール時に Android Chrome の画面を下にドラッグしてページを更新する機能が誤爆するため
    // bodyの 'touchmove' イベントを利用できないようにする
    document.addEventListener('touchmove', LivePageComponent.disableTouchMove, false);
    document.body.setAttribute('style', 'overflow: hidden;');

    this.routeParamsSub = this.route.params.subscribe(value => {
      const liveId = value.liveId;
      this.cookieKey = liveId;
      // 更新ボタンが押された時の処理
      this.execLoadEventAction();

      MagliveApiClient.getAuthority().subscribe(authorities => {
        if (authorities.status === 503 ) {
          this.isMaintenance = true;
        }
        if (authorities !== "NOT_LOGIN") {
          this.authority = authorities;
        } else {
          this.authorityPollingTimer = interval(this.authorityPollingMillis).subscribe(() => {
            MagliveApiClient.getAuthority().subscribe(newAuthorities => {
              if (newAuthorities === "NOT_LOGIN") {
                // 非ログイン状態になったら非有効化
                this.authorityPollingTimer.unsubscribe();
              }
            });
          });
        }

        this.live$ = this.afs.doc<Live>(`lives/${liveId}`).valueChanges().pipe(
          mergeMap(live => {
            if (!live) {
              return of('NOT_FOUND');
            }
            this.isScreenRotated = live.options.isScreenRotated
            // isAwsがtrueでも旧基盤での予約分はcastify視聴にする必要がある
            if (live.isNewSystem === undefined || live.isNewSystem === null) {
              this.isAws = false;
            }

            if (this.authority) {
              new FirestoreAccess(this.afs).follows(this.authority.userId, live.magazineId)
                .subscribe(follows => this.following = follows.every(x => x.following))
            }

            this.magazine$ = MagliveApiClient.getMagazine(live.magazineId);

            if (this.previousLive === undefined || this.previousLive.id !== live.id) {
              this.sneakPeekFinishedReason = undefined; // チラ見せダイアログを消す (開きっぱなしの場合に新しいライブでも出たままになる)
            }

            // 初回視聴時はcookieに保存されているmute情報を削除する
            if (!this.isSuccessFirstView && this.cookieService.check(live.id)) {
              this.cookieService.delete(live.id);
            }

            // ライブが終了した時の処理
            if (live.status === "FORCE_STOPPED" || live.status === "CLOSED") {
              if (this.isAws) {
                if (this.isSuccessFirstView && this.canViewLive) {
                  this.timerForDelay = interval(this.endBroadcastDelayTimeSec * 1000).subscribe(() => {
                    this.timerForDelay.unsubscribe();
                    this.timerForDelay = undefined;
                    this.closeWebsocket();
                    this.canViewLive = false;
                  })
                } else {
                  this.closeWebsocket();
                  this.canViewLive = false;
                }
                return of(live);
              // castify
              } else {
                // ライブが終了済みの場合は各種 Observable の処理は不要 (終了済みの表示を出す)
                return of(live);
              }
            }

            // 配信視聴画面の表示切り替え
            if (this.isAws) {
              if (this.timerForDelay === undefined && this.isSuccessFirstView) {
                // 遅延処理が走っている間は表示させる
                this.canViewLive = true;
              } else if (live.status === 'ON_AIR' || live.status === 'NO_STREAM' || live.status === 'RESERVED') {
                this.canViewLive = true;
              } else {
                this.canViewLive = false;
              }
            }

            // ライブの視聴権限等に変化が無い場合はそのままリターンする
            if (this.isPreviousLive(live)) return of(this.previousLiveViewResult);

            // 以下、初回視聴時もしくは視聴権限が変化した場合の処理

            const previousLive = this.previousLive;
            this.previousLive = live;
            this.sneakPeekFinishedReason = undefined; // チラ見せダイアログを消す

            const returns = (result: LiveViewResult): LiveViewResult => {
              this.previousLiveViewResult = result;
              return result;
            };

            // NOTE: ここ使用してないのでは・・・？
            this.route.queryParams.subscribe(params => {this.token = params.token})
            if (this.token) {
              // 共有用tokenがある場合は視聴処理に入る（検証はサーバ側webhookで行われる）
              return of(returns(this.executeView(live, previousLive)));
            }

            // チケット限定配信の場合、所持チェックをする
            if (live.options.viewerSegment === 'TICKET') {
              this.ticketEventId = live.ticketEventId;
              MagticketApiClient.getTickets(live.ticketEventId)
              .pipe(filter(x => x !== 'NOT_FOUND')),
              switchMap(x => {
                this.tickets = x as LiveTicketOrder[];
                if (!this.tickets || this.tickets.length === 0) {
                  return of('NOT_TICKET');
                } else {
                  return of(returns(this.executeView(live, previousLive)));
                }
              });
              // return of('NOT_INITIALIZED');
            }

            const canView = LivePageComponent.canView(live.magazineId, live.options.viewerSegment, authorities);
            if (canView === 'OK') {
              this.canViewLive = true;
              this.isSuccessFirstView = true;
              this.isSubscriber = true;
              // 本来見れるユーザーは視聴処理に入る
              return of(returns(this.executeView(live, previousLive)));
            }

            if (this.isAws) {
              // 視聴途中に読者限定に変更された場合は遅延させる
              if (this.isSuccessFirstView && canView === "NOT_SUBSCRIBER") {
                this.timerForDelay = interval(this.endBroadcastDelayTimeSec * 1000).subscribe(() => {
                  this.timerForDelay.unsubscribe();
                  this.timerForDelay = undefined;
                  this.isSubscriber = false;
                  this.closeWebsocket();
                })
              }
              // タイマーが走っていない場合はcanViewの判定に基づく結果を返す
              if (this.timerForDelay === undefined) {
                return of(returns(canView));
              // タイマーが走っている間は視聴を続行させる
              } else if (this.timerForDelay) {
                return of(returns(this.executeView(live, previousLive)));
              }
            }

            // チラ見せが設定されていない場合、視聴処理を試みる前に全ての判定ができるので、先に判定する
            if (live.options.secondsViewWithoutAuthority === 0) {
              if (this.sneakPeekTimer) {
                this.sneakPeekTimer.unsubscribe()
              }
              return of(returns(canView));
            }

            // 一度このライブにおいてチラ見せでエラーになっている場合、ここまでで視聴できなかった場合は、チラ見せロジックに入らずにエラーにする
            // TODO 視聴を試みた上でエラーにする方が望ましい (2回以上チラ見せが可能になった場合にここを直す必要がある)
            const sneakPeekHistory = Mag2LocalStorage.getSneakPeek(live.id);
            if (sneakPeekHistory !== null) {
              this.sneakPeekFinishedReason = sneakPeekHistory.reason;
              return of(returns(sneakPeekHistory.reason));
            }

            // NOTE: 以下は使用されてなさそう
            // チラ見せが設定されている場合、サーバサイドからチラ見せの残り秒数を取得する
            return MagliveApiClient.getLiveViewHistoryList(live.id).pipe(map(viewHistoryList => {
              if (viewHistoryList === 'NOT_FOUND') {
                return returns("NOT_FOUND");
              }

              const sneakPeekReason = (canView === 'NOT_LOGIN')
                ? 'NOT_LOGIN_SNEAK_PEEK' : (canView === 'NOT_SUBSCRIBER')
                  ? 'NOT_SUBSCRIBER_SNEAK_PEEK' : new Error(`Unknown view result: ${canView}`);

              if (typeof sneakPeekReason === "object") {
                throw sneakPeekReason;
              }

              // サーバサイドからチラ見せ秒数が残っている旨のレスポンスが返ってきたので、視聴を試みる
              // (残り秒数が1秒以下の場合、視聴処理中にエラーになりかねないのでエラーに倒す)
              if (1 < viewHistoryList.remainsViewAuthoritySeconds) {
                if (live.status === 'NO_STREAM') {
                  // まだ配信が開始しておらず、視聴 URL にもアクセスしないため、サーバサイドでもチラ見せ秒数は消化しない
                  return returns(this.executeView(live, previousLive));
                }
                if (this.sneakPeekTimer) {
                  this.sneakPeekTimer.unsubscribe();
                }
                this.sneakPeekTimer = interval(viewHistoryList.remainsViewAuthoritySeconds * 1000).subscribe(() => {
                  // チラ見せ秒数が経過したらエラーにする
                  this.sneakPeekTimer.unsubscribe();
                  if (this.playerComponent && this.playerComponent.length === 1) {
                    this.playerComponent.first.destroy();
                  }

                  Mag2LocalStorage.addSneakPeek(live.id, {reason: sneakPeekReason, createdAt: new Date().getTime()});
                  this.sneakPeekFinishedReason = sneakPeekReason;
                  // TODO チラ見せの視聴途中で理由が解消済みの可能性がある (入会したなど) ので、視聴処理をやり直す方が望ましい
                });

                return returns(this.executeView(live, previousLive));
              }

              // チラ見せ秒数が不足しているので、エラー理由を返す
              this.sneakPeekFinishedReason = sneakPeekReason;
              return returns(sneakPeekReason);
            }));
          })
        );
      });
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('touchmove', LivePageComponent.disableTouchMove, false);
    document.body.removeAttribute('style');

    if (this.countPollingTimer) {
      this.countPollingTimer.unsubscribe();
    }
    if (this.sneakPeekTimer) {
      this.sneakPeekTimer.unsubscribe();
    }
    if (this.authorityPollingTimer) {
      this.authorityPollingTimer.unsubscribe();
    }
    if (this.isAws) {
      this.closeWebsocket();
    }
    if (this.timerForDelay) {
      this.timerForDelay.unsubscribe();
      this.timerForDelay = undefined;
    }
    if (this.checkUserAgentTimer) {
      clearTimeout(this.checkUserAgentTimer);
    }
    if (this.getWatchDataSub) {
      this.getWatchDataSub.unsubscribe();
    }
    if (this.getConfigSub) {
      this.getConfigSub.unsubscribe();
    }
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
    if (this.getTicketsSub) {
      this.getConfigSub.unsubscribe();
    }
  }

  checkUserAgent() {
    this.checkUserAgentTimer = setTimeout(() => {
      const ua = window.navigator.userAgent.toLowerCase();
      if (ua.indexOf('msie') !== -1 || ua.indexOf('trident') !== -1) {
        window.alert('ご利用のブラウザはストリーミングに対応しておりません。\n引き続きまぐまぐ！Liveをご利用になるには、ブラウザをChrome等に変更の上お楽しみください。');
      }
    }, 1000);
  }

  private isPreviousLive(live): boolean {
    if (
      this.previousLive
      && this.previousLive.id === live.id
      && this.previousLive.broadcastId === live.broadcastId
      && this.previousLive.status === live.status
      && this.previousLive.options.isViewerCountEnabled === live.options.isViewerCountEnabled
      && this.previousLive.options.secondsViewWithoutAuthority === live.options.secondsViewWithoutAuthority
      && this.previousLive.options.viewerSegment === live.options.viewerSegment
    ) {
      this.logger.message(
        this.constructor.name,
        'isPreviousLive',
        `viewer authority of this new live is exactly same as previous. id: ${live.id}`
      )
      return true;
    } else {
      return false;
    }
  }

  // 更新ボタンが押された際の処理がiOSとAndroidで違うので処理の分岐
  private execLoadEventAction() {
    const ua = window.navigator.userAgent.toLowerCase();

    if (ua.indexOf('iphone') !== -1 && ua.indexOf('safari') !== -1) {
      // iOS・safariの場合
      window.addEventListener('pagehide', () => {
        if (this.cookieService.check(this.cookieKey)) {
          this.cookieService.delete(this.cookieKey);
        }
      });
    } else {
      // その他の場合
      window.addEventListener("beforeunload", () => {
        if (this.cookieService.check(this.cookieKey)) {
          this.cookieService.delete(this.cookieKey);
        }
      });
    }
  }

  private static canView(magazineId: number, liveOptionsViewerSegment: LiveOptionsViewerSegment, authorities: LiveUserAuthority | "NOT_LOGIN"): "OK" | "NOT_LOGIN" | "NOT_SUBSCRIBER" | "NOT_TICKET" {
    if (authorities === "NOT_LOGIN") {
      // 未ログイン状態であり、ライブがログインユーザー限定・有料購読ユーザー限定・チケット限定だったらエラー
      if (liveOptionsViewerSegment === "LOGIN") {
        return "NOT_LOGIN"
      } else if (liveOptionsViewerSegment === "SUBSCRIBER") {
        return "NOT_SUBSCRIBER"
      } else if (liveOptionsViewerSegment === "TICKET") {
        return "NOT_TICKET"
      }
    } else {
      // ログイン状態の場合、ユーザーの権限をチェックして事前にエラーにする
      if (liveOptionsViewerSegment === "SUBSCRIBER"
        && !authorities.subscribingReleasedPaymentMagazineIds.includes(magazineId)
        && !authorities.publishingReleasedPaymentMagazineIds.includes(magazineId)
      ) {
        return "NOT_SUBSCRIBER"
      }
    }

    // 上記のいずれにも該当しなければ恐らく視聴可能なので通す
    return "OK";
  }

  /**
   * 視聴開始時の処理 (コメント数・いいね数・視聴者数の取得)
   * @param live 
   * @param previousLive 
   * @returns
   */
  private executeView(live: Live, previousLive: Live | undefined): Live {
    this.commentCount$ = this.firestoreAccess.getCommentCount(live.id);
    this.reactionGoodCount$ = this.firestoreAccess.getReactionGoodCount(live.id);
    if (this.isAws && this.websocketSubject === undefined) {
      // 視聴者数の取得
      this.connectWebsocket(live);
    }
    this.executeViewPolling(live, previousLive);
    return live;
  }

  private executeViewPolling(live: Live, previousLive: Live | undefined) {
    if (this.isAws) {
      this.viewerCount$ = undefined;
      return
    } else {
      this.viewerCount2$ = undefined;
    }
    if (
      previousLive
      && previousLive.id === live.id
      && previousLive.options.isViewerCountEnabled === live.options.isViewerCountEnabled
    ) {
      return;
    }
    // 視聴者数は Castify から取得するので 10 秒おきにカウントを更新
    if (this.countPollingTimer !== undefined) {
      this.countPollingTimer.unsubscribe();
    }
    if (live.options.isViewerCountEnabled) {
      this.viewerCount$ = MagliveApiClient.getLiveViewerCount(live.id);
      this.countPollingTimer = interval(this.countPollingMillis).subscribe(() => {
        MagliveApiClient.getLiveViewerCount(live.id).subscribe(x => this.viewerCount$ = of(x));
      });
    } else {
      this.viewerCount$ = undefined;
    }
  }

  /**
   * 配信情報取得APIからwebsocketトークンを取得し、websocketへ接続する
   * @param live 
   */
  private connectWebsocket(live): void {
    this.logger.message(this.constructor.name, 'connectWebsocket', 'connect process start')
    this.getWatchDataSub = MagliveApiClient.getWatchData(live.id).pipe(
      map(watchData => {
        if (watchData !== "NOT_FOUND" && this.websocketSubject === undefined) {
          this.websocketSubject = this.getViewerCountFromWebsocket(live.roomId, this.authority?.userId, watchData.websocketToken);
        }
        // 10秒ごとにリクエストを送る (視聴者数を更新する)
        if (this.websocketSubject && this.countPollingTimer === undefined) {
          this.countPollingTimer = interval(this.countPollingMillisWs).subscribe(() => {
            this.websocketSubject.next(this.wsReqMessage);
          });
        }
      })
    ).subscribe();
  }

  /**
   * websocket接続で視聴者数を取得する
   * @param roomId 
   * @param userId 
   * @param token 
   * @returns 
   */
  private getViewerCountFromWebsocket(roomId: string, userId: number | undefined, token: string): WebSocketSubject<any> {
    if (userId === undefined) {
      userId = 0;
    }
    const wsEndPoint = `${environment.baseURL.mag2WSDomain}?room_id=${roomId};user_id=${userId};token=${token}`;
    const wsSubject = webSocket(wsEndPoint);
    this.logger.message(this.constructor.name, 'getViewerCountFromWebsocket', 'connected');
    wsSubject.next(this.wsReqMessage);
    wsSubject.subscribe(
      x => {
        if (typeof x === "object") {
          // キャッシュが残っていた場合に目アイコンが表示されてしまうバグ対応の為
          this.viewerCount$ = undefined;
          this.viewerCount2$ = of(x);
          this.logger.message(this.constructor.name, 'getViewerCountFromWebsocket', 'view count updated');
        }
      },
      error => console.error(error),
      () => this.logger.message(this.constructor.name, 'getViewerCountFromWebsocket', 'close')
    );
    return wsSubject;
  }

  /**
   * websocketの接続を切る
   */
  private closeWebsocket(): void {
    if (this.websocketSubject) {
      this.websocketSubject.unsubscribe();
      this.websocketSubject = undefined;
      if (this.countPollingTimer) {
        this.countPollingTimer.unsubscribe();
        this.countPollingTimer = undefined;
      }
    }
  }

  onFollowClick(magazine, following) {
    if (this.authority) {
      const apiCall = following ? MagliveApiClient.followMagazine : MagliveApiClient.unfollowMagazine
      apiCall(magazine.id, "web")
        .subscribe(_ => {
          this.following = following
        })
    }
    if (following) {
      this.toggleFollowModal()
    }
    return false
  }

  toggleFollowModal() {
    this.followModalOpened = !this.followModalOpened
  }

  showGiftControl(isShow: boolean|undefined): void {
    if (isShow !== undefined) {
      this.isShowGiftComponent = isShow;
    } else {
      this.isShowGiftComponent = !this.isShowGiftComponent;
    }
  }

  showGiftConfirmControl(isShow: boolean|undefined): void {
    if (isShow !== undefined) {
      this.isShowGiftConfirmComponent = isShow;
    } else {
      this.isShowGiftConfirmComponent = !this.isShowGiftConfirmComponent;
    }
  }

  /**
   * ギフトアニメーションを描画する
   */
  setAnimation(request: GiftAnimationReq) {
    this.playerChildComponent.setAnimation(request);
  }

  /**
   * ログイン導入アラートの表示の出し分け
   */
  toggleLoginAlert(text?: string) {
    if (text) this.actionText = text
    this.isShowLoginAlert = !this.isShowLoginAlert
  }

  /**
   * 画質調整(歯車)の内容表示切り替え
   */
  toggleVariantModal(): void {
    this.isShowVariantModal = !this.isShowVariantModal
  }

  /**
   * 画質の変更
   * @param variant 
   */
  changeVariant(variant: string) {
    this.selectedVariant = variant
    this.toggleVariantModal()
    this.playerChildComponent.changeVariant(variant)
  }

  /**
   * 共有モーダルの開閉
   */
  toggleShareModal() {
    this.isShowShareModal = !this.isShowShareModal
  }

  /**
   * ダイアログの開閉
   */
  toggleDialog() {
    this.isShowDialog = !this.isShowDialog
  }
}
